import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mensagem } from '@/core/models/mensagens/mensagem.model';
import { CotinButton } from '@cotin/biblioteca-componentes-react';

const MODAL_WIDTH = 320;
const MODAL_OFFSET = 8;
const MIN_MARGIN = 16;
const HEADER_HEIGHT = 64;

export interface NotificationDropdownProps {
    isOpen: boolean;
    messages: Mensagem[];
    anchorRef: React.RefObject<HTMLButtonElement | null>;
    onClose: () => void;
    onViewMessage: (message: Mensagem) => void;
    onViewAll: () => void;
}

function previewPlainText(value: string | null | undefined, fallback: string): string {
    const raw = value != null && String(value).trim() !== '' ? String(value) : fallback;
    try {
        const doc = new DOMParser().parseFromString(raw, 'text/html');
        const text = doc.body?.textContent ?? '';
        return text.replace(/\s+/g, ' ').trim();
    } catch {
        return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
}

function calculatePosition(rect: DOMRect): { top: number; left: number } {
    let left = rect.right - MODAL_WIDTH;
    if (left < MIN_MARGIN) {
        left = MIN_MARGIN;
    }

    let top = rect.bottom + MODAL_OFFSET;
    if (top < HEADER_HEIGHT) {
        top = HEADER_HEIGHT + MODAL_OFFSET;
    }

    return { top, left };
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
    isOpen,
    messages,
    anchorRef,
    onClose,
    onViewMessage,
    onViewAll,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        if (!anchorRef.current) {
            return;
        }
        setPosition(calculatePosition(anchorRef.current.getBoundingClientRect()));
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        updatePosition();
        requestAnimationFrame(() => {
            updatePosition();
        });
    }, [isOpen, updatePosition, messages.length]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onViewportChange = () => {
            requestAnimationFrame(updatePosition);
        };

        window.addEventListener('resize', onViewportChange);
        window.addEventListener('scroll', onViewportChange, true);

        return () => {
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('scroll', onViewportChange, true);
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) {
                return;
            }
            if (anchorRef.current?.contains(target)) {
                return;
            }
            onClose();
        };

        document.addEventListener('click', onClickOutside);
        return () => document.removeEventListener('click', onClickOutside);
    }, [isOpen, onClose, anchorRef]);

    if (!isOpen || typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div className="cotin-notification tutorial-overlay" aria-hidden={!isOpen}>
            <div
                ref={containerRef}
                className="tutorial-tooltip"
                style={{ top: `${position.top}px`, left: `${position.left}px` }}
                role="dialog"
                aria-modal="false"
                aria-label="Notificações">
                <div className="tooltip-header">
                    <h3 className="tooltip-title">Notificações</h3>
                    <button type="button" className="tooltip-close" onClick={onClose} aria-label="Fechar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <div className="tooltip-content">
                    {messages.length === 0 ? (
                        <p className="tooltip-description">Nenhuma notificação nova.</p>
                    ) : (
                        <div className="message-list">
                            {messages.map((message) => (
                                <div className="message-item" key={message.id}>
                                    <div className="message-content">
                                        <div className="message-text-block">
                                            <div className="message-preview-row">
                                                <strong className="message-preview-label">Assunto:</strong>
                                                <span className="message-preview-assunto">
                                                    {previewPlainText(message.assunto, 'Sem assunto')}
                                                </span>
                                            </div>
                                            <div className="message-preview-row">
                                                <strong className="message-preview-label">Mensagem:</strong>
                                                <span className="message-preview-mensagem">
                                                    {previewPlainText(message.mensagem, 'Sem descrição')}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="view-message-link"
                                            onClick={() => onViewMessage(message)}
                                            aria-label="Ver mensagem">
                                            Visualizar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="tooltip-footer">
                    <div className="tooltip-progress">
                        <span className="progress-text" />
                        <div className="progress-dots" />
                    </div>
                    <div className="tooltip-actions">
                            <CotinButton
                                id="btn-notificacoes-ver-todas"
                                type="button"
                                variant="primary"
                                size="medium"
                                text="Ver Todas"
                                onClick={onViewAll}
                            />
                        </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NotificationDropdown;
